import { useEffect, useRef, useState } from 'react';
import findActivePath from './findActivePath';

// Module-level: patch history mutators once so SPA navigations are observable
// from this hook. Idempotent — guarded against double-patching across multiple
// hook instances.
const LOCATION_CHANGE_EVENT = 'uifp:locationchange';
let historyPatched = false;

function patchHistoryOnce() {
    if (historyPatched) return;
    if (typeof window === 'undefined' || typeof window.history === 'undefined') return;

    const dispatchLocationChange = () => {
        try {
            window.dispatchEvent(new Event(LOCATION_CHANGE_EVENT));
        } catch {
            // Older environments without Event constructor — fall back silently;
            // popstate/hashchange will still cover most cases.
        }
    };

    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;

    if (typeof origPush === 'function') {
        window.history.pushState = function patchedPush(...args) {
            const result = origPush.apply(this, args);
            dispatchLocationChange();
            return result;
        };
    }
    if (typeof origReplace === 'function') {
        window.history.replaceState = function patchedReplace(...args) {
            const result = origReplace.apply(this, args);
            dispatchLocationChange();
            return result;
        };
    }

    historyPatched = true;
}

// Test-only: reset the patched flag so each test file can re-patch a fresh
// jsdom-restored history. Not exported in the public API surface — accessed
// only via the explicit named import in the spec.
export function __resetHistoryPatchForTesting() {
    historyPatched = false;
}

/**
 * React hook to find the active menu path in menuData based on the current
 * window.location.pathname.
 *
 * SMNUI-25334: recomputes on SPA navigations (history pushState/replaceState,
 * popstate, hashchange) so the merged SideNav's section-expansion stays in
 * sync when the host app changes route without a full page reload.
 *
 * @param {Object} menu - The menu structure object.
 * @returns {string[]} - Array of id strings representing the path to the active item.
 */
function useActiveMenuPath(menu) {
    const [activePath, setActivePath] = useState(() => (
        typeof window !== 'undefined'
            ? findActivePath(menu, window.location.pathname)
            : []
    ));

    // Hold the latest menu in a ref so the listeners (attached once) always
    // see the current data without re-binding on every menu reference change.
    const menuRef = useRef(menu);
    menuRef.current = menu;

    // Attach listeners once. The recompute callback reads menuRef.current,
    // so a new `menu` reference each render does NOT trigger this effect.
    // We deliberately do NOT depend on `menu` here: the consumer (Shellbar)
    // passes a freshly-spread object every render
    // (`{ ...adjustedMenuData, adminMenuItem: undefined }` in _Shellbar.jsx),
    // so a `[menu]` dep would cause a re-render → new menu identity → effect
    // → setActivePath → re-render loop.
    useEffect(() => {
        if (typeof window === 'undefined') return undefined;

        patchHistoryOnce();

        const recompute = () => {
            setActivePath(findActivePath(menuRef.current, window.location.pathname));
        };

        window.addEventListener('popstate', recompute);
        window.addEventListener('hashchange', recompute);
        window.addEventListener(LOCATION_CHANGE_EVENT, recompute);

        return () => {
            window.removeEventListener('popstate', recompute);
            window.removeEventListener('hashchange', recompute);
            window.removeEventListener(LOCATION_CHANGE_EVENT, recompute);
        };
    }, []);

    return activePath;
}

export default useActiveMenuPath;
